import React, { useEffect, useState, useCallback } from 'react';
import {
  Plus, Trash2, Edit3, RefreshCw, X, Loader2, UploadCloud,
  FileText, Video, Image as ImageIcon, BookOpen,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, Btn, Input, Select, Textarea } from './shared';
import { useToast } from '../../hooks/use-toast';
import RichTextEditor from '../../components/RichTextEditor';
import AiImageButton from './AiImageButton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { value: 'article', label: 'Article', icon: FileText },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'image', label: 'Image', icon: ImageIcon },
  { value: 'guide', label: 'User Guide', icon: BookOpen },
];

const CATEGORIES = ['user-guide', 'tutorial', 'webinar', 'case-study', 'video', 'datasheet', 'other'];

const BLANK = {
  title: '', slug: '', type: 'guide', category: 'user-guide',
  excerpt: '', content: '', cover_image: '', media_url: '',
  duration: '', tags: '', published: true,
};

function slugify(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').substring(0, 120);
}

export default function AdminResources() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems((await api.get('/api/admin/resources')).data || []);
    } catch (err) { toast({ title: 'Failed to load', description: formatApiError(err) }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title?.trim()) return toast({ title: 'Title required' });
    setSaving(true);
    try {
      const body = {
        title: editing.title.trim(),
        slug: editing.slug?.trim() || slugify(editing.title),
        type: editing.type,
        category: editing.category || 'user-guide',
        excerpt: editing.excerpt || '',
        content: editing.content || '',
        cover_image: editing.cover_image || '',
        media_url: editing.media_url || '',
        duration: editing.duration || '',
        tags: (typeof editing.tags === 'string' ? editing.tags.split(',') : editing.tags || []).map((t) => t.trim()).filter(Boolean),
        published: !!editing.published,
      };
      if (editing.id) await api.patch(`/api/admin/resources/${editing.id}`, body);
      else await api.post('/api/admin/resources', body);
      toast({ title: 'Saved' });
      setEditing(null); load();
    } catch (err) { toast({ title: 'Save failed', description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this resource?')) return;
    try { await api.delete(`/api/admin/resources/${id}`); load(); toast({ title: 'Deleted' }); }
    catch (err) { toast({ title: 'Delete failed', description: formatApiError(err) }); }
  };

  const upload = async (file, field) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('folder', 'resources');
      const res = await api.post('/api/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = BACKEND + res.data.url;
      setEditing((e) => ({ ...e, [field]: url }));
      toast({ title: 'Uploaded' });
    } catch (err) { toast({ title: 'Upload failed', description: formatApiError(err) }); }
    finally { setUploading(false); }
  };

  const startEdit = (r) => setEditing({ ...r, tags: (r.tags || []).join(', ') });

  const grouped = items.reduce((acc, r) => { (acc[r.type] ||= []).push(r); return acc; }, {});

  return (
    <>
      <PageHeader
        title="Resources"
        subtitle="User guides, video tutorials, articles and downloadable images shown on the public Resources page"
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
            <Btn onClick={() => setEditing({ ...BLANK })}><Plus size={14} /> Add resource</Btn>
          </div>
        }
      />

      {loading ? <Loading /> : items.length === 0 ? (
        <AdminCard><Empty>No resources yet — add your first user guide, video or article.</Empty></AdminCard>
      ) : (
        <div className="space-y-4">
          {TYPES.map(({ value, label, icon: Ic }) => {
            const list = grouped[value] || [];
            if (list.length === 0) return null;
            return (
              <AdminCard key={value} title={<span className="flex items-center gap-2"><Ic size={16} /> {label} <span className="text-xs text-slate-400">({list.length})</span></span>}>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map((r) => (
                    <div key={r.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                      {r.cover_image && (
                        <div className="aspect-[16/9] bg-slate-800 overflow-hidden">
                          <img src={r.cover_image} alt={r.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-3">
                        <p className="font-bold text-white text-sm truncate">{r.title}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5 capitalize">{r.category} · {r.duration || '—'}</p>
                        <div className="flex gap-1 mt-2">
                          <Btn size="sm" variant="ghost" onClick={() => startEdit(r)}><Edit3 size={12} /></Btn>
                          <Btn size="sm" variant="danger" onClick={() => remove(r.id)}><Trash2 size={12} /></Btn>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </AdminCard>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => !saving && setEditing(null)}>
          <div className="flex-1 bg-black/60 backdrop-blur-sm" />
          <aside className="w-full max-w-2xl bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">{editing.id ? 'Edit resource' : 'New resource'}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                {TYPES.map(({ value, label, icon: Ic }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setEditing({ ...editing, type: value })}
                    className={`p-3 rounded-xl border text-center transition ${editing.type === value
                      ? 'bg-gradient-to-br from-violet-500/30 to-fuchsia-500/15 border-violet-500/60 text-white'
                      : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'}`}
                  >
                    <Ic size={18} className="mx-auto mb-1" />
                    <span className="text-xs font-bold">{label}</span>
                  </button>
                ))}
              </div>

              <Input label="Title *" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="auto from title" />
                <Select label="Category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </div>
              <Textarea label="Excerpt" rows={2} value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />

              {(editing.type === 'article' || editing.type === 'guide') && (
                <div>
                  <span className="text-xs font-semibold text-slate-300 mb-1.5 block">Content</span>
                  <RichTextEditor
                    value={editing.content}
                    onChange={(v) => setEditing({ ...editing, content: v })}
                    placeholder="Write your guide / article…"
                  />
                </div>
              )}

              {editing.type === 'video' && (
                <Input label="Video URL (YouTube, Vimeo, MP4)" value={editing.media_url} onChange={(e) => setEditing({ ...editing, media_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." />
              )}

              {editing.type === 'image' && (
                <div>
                  <span className="text-xs font-semibold text-slate-300 mb-1.5 block">Image / file</span>
                  {editing.media_url && <img src={editing.media_url} alt="" className="w-full h-40 object-contain bg-slate-800 rounded-lg mb-2" />}
                  <div className="flex flex-wrap gap-2">
                    <Input value={editing.media_url} onChange={(e) => setEditing({ ...editing, media_url: e.target.value })} placeholder="https://…" className="flex-1 min-w-[200px]" />
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-200 hover:bg-white/10 cursor-pointer">
                      {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                      <input type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0], 'media_url')} />
                    </label>
                    <AiImageButton
                      defaultPrompt={editing.title ? `Marketing illustration for resource: ${editing.title}` : ''}
                      folder="resources"
                      onGenerated={(url) => setEditing((e) => ({ ...e, media_url: url }))}
                      label="Generate with AI"
                    />
                  </div>
                </div>
              )}

              {(editing.type === 'article' || editing.type === 'guide' || editing.type === 'video') && (
                <div>
                  <span className="text-xs font-semibold text-slate-300 mb-1.5 block">Cover image</span>
                  {editing.cover_image && <img src={editing.cover_image} alt="" className="w-full h-32 object-cover rounded-lg mb-2 border border-white/10" />}
                  <div className="flex flex-wrap gap-2">
                    <Input value={editing.cover_image} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} placeholder="https://…" className="flex-1 min-w-[200px]" />
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-200 hover:bg-white/10 cursor-pointer">
                      {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                      <input type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0], 'cover_image')} />
                    </label>
                    <AiImageButton
                      defaultPrompt={editing.title ? `Wide cover image for ${editing.type}: ${editing.title}. Professional, marketing-quality, no text.` : ''}
                      folder="resources"
                      onGenerated={(url) => setEditing((e) => ({ ...e, cover_image: url }))}
                      label="Generate with AI"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Input label="Duration / size" value={editing.duration} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} placeholder="5 min read · 8:32 · 2 MB" />
                <Input label="Tags (comma)" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="w-4 h-4 accent-fuchsia-500" />
                <span className="text-sm text-slate-300">Published</span>
              </label>
            </div>

            <div className="mt-6 pt-4 border-t border-white/10 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save'}</Btn>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
